'use client'

import { useMemo } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod/v4'

import type { AvatarList } from '@helper/utils/domain'
import { UpdateTeamSchema } from '@package/drizzle/schema'
import { Button, Field, FieldError, FieldLabel, FieldSet, Input } from '@ui/web/components'
import { AvatarSelector } from '@ui/web/lib'
import { tryCatch } from '@helper/utils/core'
import cn from '@config/tailwind/cn'

export const TeamDetailsFormSchema = UpdateTeamSchema.pick({
  name: true,
  image: true,
})
  .extend({
    imageFile: z.instanceof(File).nullable(),
  })
  .superRefine((data, ctx) => {
    if (!data.name) {
      ctx.addIssue({ code: 'custom', message: 'Nama harus diisi', path: ['name'] })
    }
  })

export type TeamDetailsFormValues = z.infer<typeof TeamDetailsFormSchema>

function formValuesEqual(current: TeamDetailsFormValues, initial: TeamDetailsFormValues): boolean {
  if (current.name !== initial.name) return false
  if (current.image !== initial.image) return false
  if (current.imageFile instanceof File) return false

  return true
}

interface TeamDetailsFormProps {
  onSubmit: (values: TeamDetailsFormValues) => Promise<void> | void
  initialValues?: Partial<TeamDetailsFormValues>
  submitButtonText?: string
  submitButtonClassName?: string
  /** Avatar data required for avatar selector */
  avatarData?: AvatarList[]
  /** Placeholder fallback for avatar selector */
  avatarPlaceholder?: string
}

export function TeamDetailsForm({
  onSubmit,
  initialValues = {},
  submitButtonText = 'Perbarui',
  submitButtonClassName,
  avatarData = [],
  avatarPlaceholder = '?',
}: TeamDetailsFormProps) {
  const initialFormValues = useMemo(
    (): TeamDetailsFormValues => ({
      name: initialValues.name ?? '',
      image: initialValues.image ?? null,
      imageFile: initialValues.imageFile ?? null,
    }),
    [initialValues.name, initialValues.image, initialValues.imageFile],
  )

  const form = useForm({
    defaultValues: initialFormValues as z.input<typeof TeamDetailsFormSchema>,
    validators: {
      onSubmit: TeamDetailsFormSchema,
      onChange: TeamDetailsFormSchema,
      onBlur: TeamDetailsFormSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const [_, error] = await tryCatch(async () => await onSubmit(value))

      if (error) {
        // @ts-expect-error - error.message is a string
        formApi.setErrorMap({ onSubmit: error.message })
      }
    },
  })

  const errorMap = useStore(form.store, (state) => state.errorMap)
  const canSubmit = useStore(form.store, (state) => state.canSubmit)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const values = useStore(form.store, (state) => state.values) as TeamDetailsFormValues
  const hasChanges = !formValuesEqual(values, initialFormValues)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <FieldSet>
        <div className='srd:flex srd:w-full srd:flex-col srd:gap-y-4'>
          {/* Avatar Selector */}
          <div className='srd:flex srd:w-full srd:items-center srd:justify-center'>
            <form.Field name='image'>
              {(field) => (
                <AvatarSelector
                  handleAvatarSelectAction={(avatar) => field.handleChange(avatar.src)}
                  avatarData={avatarData}
                  initialAvatarKey={
                    field.state.value ? (avatarData.find((a) => a.src === field.state.value)?.key ?? null) : null
                  }
                  placeholderFallback={avatarPlaceholder}
                  size={100}
                />
              )}
            </form.Field>
          </div>

          {/* Team Name */}
          <form.Field name='name'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Nama Kelas</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>
        </div>
      </FieldSet>

      {errorMap?.onSubmit && typeof errorMap.onSubmit === 'string' ? (
        <p className='srd:text-main-red-100 srd:mt-4 srd:text-xs'>{errorMap.onSubmit}</p>
      ) : null}

      <Button
        type='submit'
        disabled={!canSubmit || !hasChanges}
        loading={isSubmitting}
        className={cn('srd:bg-main-blue-100 srd:mt-8 srd:w-full srd:rounded-full', submitButtonClassName)}
      >
        {submitButtonText}
      </Button>
    </form>
  )
}
